import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';
import { getFavoriteEntries } from '@/utils/content';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '漫画分类', to: '/category/comics' },
  { label: '动漫资讯', to: '/category/news' },
  { label: '排行榜', to: '/category/comics?sort=popularity' },
  { label: '专题', to: '/#topics' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites, removeFavorite } = useFavorites();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (favOpen && drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        setFavOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [favOpen]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
  };

  const favEntries = getFavoriteEntries(favorites);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center gap-4 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-xl text-background-50">
              <i className="ri-leaf-line" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-800 tracking-wide text-foreground-950">漫蛙</span>
              <span className="text-[10px] font-label tracking-widest text-foreground-500">MANWA</span>
            </span>
          </Link>

          <nav className="ml-4 hidden items-center gap-1 lg:flex">
            {NAV_ITEMS.map((item) => {
              const active = item.to === location.pathname + location.search
                || (item.to === '/' && location.pathname === '/');
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label font-500 transition-colors ${
                    active
                      ? 'bg-primary-100 text-primary-800'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden items-center md:flex">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 transition-colors focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索漫画 / 资讯"
                  className="w-40 bg-transparent text-sm font-label text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-52"
                />
              </div>
              <button
                type="submit"
                className="ml-2 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label font-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-lg text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className="ri-search-line" />
            </button>

            <div className="relative" ref={drawerRef}>
              <button
                type="button"
                onClick={() => setFavOpen((v) => !v)}
                aria-label="我的收藏"
                className="flex h-10 cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-3 text-sm font-label text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-heart-3-line text-base text-accent-600" />
                <span className="hidden sm:inline">收藏</span>
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-[11px] font-700 text-foreground-950">
                  {favorites.length}
                </span>
              </button>

              {favOpen && (
                <div className="absolute right-0 top-12 w-[320px] rounded-lg border border-background-300 bg-background-50 p-3 md:w-[360px]">
                  <div className="mb-2 flex items-center justify-between px-1">
                    <h3 className="font-heading text-base font-700 text-foreground-950">我的收藏</h3>
                    <span className="text-xs font-label text-foreground-500">共 {favEntries.length} 项</span>
                  </div>
                  {favEntries.length === 0 ? (
                    <p className="px-1 py-6 text-center text-sm font-label text-foreground-500">
                      还没有收藏任何内容，去详情页点个小心心吧
                    </p>
                  ) : (
                    <ul className="max-h-[320px] overflow-y-auto">
                      {favEntries.map((entry) => (
                        <li key={entry.id} className="flex items-center gap-3 rounded-md p-2 hover:bg-background-100">
                          <Link to={`/detail/${entry.type}/${entry.id}`} className="h-12 w-12 shrink-0 overflow-hidden rounded-md">
                            <img src={entry.cover} alt={entry.title} className="h-full w-full object-cover object-top" />
                          </Link>
                          <Link to={`/detail/${entry.type}/${entry.id}`} className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-500 text-foreground-900">{entry.title}</span>
                            <span className="text-xs font-label text-foreground-500">
                              {entry.type === 'comics' ? '漫画作品' : '动漫资讯'}
                            </span>
                          </Link>
                          <button
                            type="button"
                            onClick={() => removeFavorite(entry.id)}
                            aria-label="移除收藏"
                            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-200 hover:text-accent-700"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="菜单"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-lg text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
            >
              <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
            </button>
          </div>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                autoFocus
                placeholder="搜索漫画 / 资讯"
                className="w-full bg-transparent text-sm font-label text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label font-500 text-background-50"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      <div
        className={`fixed inset-0 z-50 lg:hidden ${menuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!menuOpen}
      >
        <div
          className={`absolute inset-0 bg-foreground-950/40 transition-opacity ${menuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setMenuOpen(false)}
        />
        <aside
          className={`absolute right-0 top-0 h-full w-72 max-w-[85%] bg-background-50 px-5 py-6 transition-transform duration-300 ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="mb-6 flex items-center justify-between">
            <span className="font-heading text-lg font-800 text-foreground-950">导航</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="关闭菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-lg text-foreground-600 hover:bg-background-100"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="cursor-pointer rounded-md px-3 py-3 text-base font-label text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-800"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </aside>
      </div>
    </header>
  );
}